/**
 * Landing.tsx — Public marketing homepage.
 *
 * Rebuilt in Pass 7. Replaces the v1 landing (globe hero + feature grid).
 *
 * Structure:
 *   1. Nav (sticky, "Sign in" only on the right)
 *   2. Hero — eyebrow + headline + sub + 2 CTAs + product screenshot (client dashboard)
 *   3. Problem — long-form text block
 *   4. Solution 3-up
 *   5. Product tour — 4 tabs: Matched outlets / Distribution gaps / Ask Atlas / Market intelligence
 *   6. Features — 6 cards
 *   7. Final CTA — big cyan-glow block, one button, email fallback
 *   8. Footer — 3 columns
 *
 * Key decisions locked in with product:
 *   - Tagline "Where retail meets intelligence" is a letterspaced cyan eyebrow, NOT a pill, NO dot
 *   - Primary CTA = "Request a demo"; Secondary CTA = "Tour the product ↓" (scrolls to #product-tour)
 *   - Nav top-right: Sign in only (no Request a demo button — it's everywhere else)
 *   - No "Trusted by" logo bar — skip until we have real customer logos
 *   - All tour data is made-up: generic outlet names, generic US cities, synthetic Census IDs
 *   - Dashboard mockup on right shows CLIENT view (not admin), i.e. scoped to one client's data
 *   - Real PNG logos used everywhere (accept slight fuzz in tiny sidebar mockup)
 *   - No hardcoded platform-wide stats anywhere (creates maintenance burden)
 *   - Email for fallback: admin@meridianregistry.com
 */

import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';

/* ══════════════════════════════════════════════════════════════════
 * DESIGN TOKENS (local to this file — this is a standalone public page,
 * so it doesn't inherit theme.ts from the app shell. The colors mirror
 * the app theme so the visual language is consistent.)
 * ══════════════════════════════════════════════════════════════════ */

const C = {
  bgBase: '#080C18',
  bgPanel: '#0B0F1F',
  bgCard: '#0F1628',
  bgElevated: '#141C33',
  borderSubtle: 'rgba(148, 163, 184, 0.08)',
  borderDefault: 'rgba(148, 163, 184, 0.12)',
  borderStrong: 'rgba(148, 163, 184, 0.20)',
  textPrimary: '#E8EFF8',
  textSecondary: '#94A3B8',
  textTertiary: '#64748B',
  textQuaternary: '#475569',
  cyan: '#00D4FF',
  cyanLight: '#7DE5F5',
  cyanSoft: 'rgba(0, 212, 255, 0.10)',
  cyanGlow: 'rgba(0, 212, 255, 0.35)',
  success: '#10B981',
  warning: '#F59E0B',
  danger: '#EF4444',
};

const F = {
  ui: "'DM Sans', -apple-system, system-ui, sans-serif",
  data: "'Space Mono', 'SF Mono', ui-monospace, monospace",
};

export default function Landing() {
  const navigate = useNavigate();

  // Load fonts and set title. Fonts are idempotent — we check before adding.
  useEffect(() => {
    document.title = 'Meridian Registry | Where Retail Meets Intelligence';

    const loadFont = (href: string) => {
      if (document.querySelector(`link[href="${href}"]`)) return;
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      document.head.appendChild(link);
    };
    loadFont('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Space+Mono:wght@400;700&display=swap');

    return () => { document.title = 'Meridian Registry'; };
  }, []);

  return (
    <div style={{
      background: C.bgBase,
      color: C.textPrimary,
      fontFamily: F.ui,
      minHeight: '100vh',
      position: 'relative',
      overflowX: 'hidden',
      WebkitFontSmoothing: 'antialiased',
      letterSpacing: '-0.003em',
    }}>
      {/* Ambient glow — fixed, top-right only, subtle */}
      <div aria-hidden style={{
        position: 'fixed',
        top: -300, right: -400,
        width: 900, height: 900,
        background: 'radial-gradient(circle, rgba(0,212,255,0.08) 0%, transparent 65%)',
        pointerEvents: 'none',
        zIndex: 0,
      }} />

      <Nav navigate={navigate} />

      <main style={{ position: 'relative', zIndex: 1 }}>
        <Hero navigate={navigate} />
        <Problem />
        <Solution />
        <ProductTour />
        <Features />
        <FinalCTA navigate={navigate} />
      </main>

      <Footer />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
 * NAV — sticky, blurred, "Sign in" only on the right
 * ═══════════════════════════════════════════════════════════════ */

function Nav({ navigate }: { navigate: (path: string) => void }) {
  const linkStyle: React.CSSProperties = {
    fontSize: 13, fontWeight: 500, color: C.textSecondary,
    textDecoration: 'none', cursor: 'pointer',
    transition: 'color 120ms ease',
  };
  const onEnter = (e: React.MouseEvent<HTMLElement>) => { e.currentTarget.style.color = C.textPrimary; };
  const onLeave = (e: React.MouseEvent<HTMLElement>) => { e.currentTarget.style.color = C.textSecondary; };

  return (
    <nav style={{
      position: 'sticky', top: 0, zIndex: 50,
      background: 'rgba(8, 12, 24, 0.72)',
      backdropFilter: 'blur(16px)',
      WebkitBackdropFilter: 'blur(16px)',
      borderBottom: `1px solid ${C.borderSubtle}`,
    }}>
      <style>{`
        @media (max-width: 720px) {
          .meridian-nav-inner     { padding: 12px 16px !important; gap: 12px; }
          .meridian-nav-links     { display: none !important; }
          .meridian-nav-brand-txt { display: none !important; }
        }
      `}</style>
      <div className="meridian-nav-inner" style={{
        maxWidth: 1280, margin: '0 auto',
        padding: '14px 32px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 16,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 40, minWidth: 0 }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', flexShrink: 0 }}>
            <img
              alt="Meridian"
              src="/Meridian_M_logo-transparent.png"
              style={{ height: 28, width: 'auto' }}
            />
            <span className="meridian-nav-brand-txt" style={{
              fontSize: 14, fontWeight: 700,
              letterSpacing: '0.22em',
              color: C.textPrimary,
              textTransform: 'uppercase',
            }}>Meridian</span>
          </Link>
          <div className="meridian-nav-links" style={{ display: 'flex', gap: 28 }}>
            <a href="#product-tour" style={linkStyle} onMouseEnter={onEnter} onMouseLeave={onLeave}>Product</a>
            <a href="#features" style={linkStyle} onMouseEnter={onEnter} onMouseLeave={onLeave}>Features</a>
            <Link to="/api-docs" style={linkStyle} onMouseEnter={onEnter} onMouseLeave={onLeave}>API</Link>
            <Link to="/faq" style={linkStyle} onMouseEnter={onEnter} onMouseLeave={onLeave}>FAQ</Link>
          </div>
        </div>
        <div style={{ flexShrink: 0 }}>
          <GhostBtn onClick={() => navigate('/login')}>Sign in</GhostBtn>
        </div>
      </div>
    </nav>
  );
}

/* ═══════════════════════════════════════════════════════════════
 * HERO
 * ═══════════════════════════════════════════════════════════════ */

function Hero({ navigate }: { navigate: (path: string) => void }) {
  return (
    <section style={{
      padding: '80px 32px 120px',
      maxWidth: 1280, margin: '0 auto',
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr)',
      gap: 60,
      alignItems: 'center',
      position: 'relative',
      zIndex: 1,
    }}
    // Responsive 2-column via media query — React inline styles don't support @media,
    // so we use a CSS-in-JS trick: a class toggle + a <style> tag. Simpler here to use
    // grid-template-columns via CSS because we want the lg breakpoint behavior.
    className="meridian-hero">
      <style>{`
        @media (min-width: 1024px) {
          .meridian-hero {
            grid-template-columns: minmax(0, 1fr) minmax(0, 1.1fr) !important;
            gap: 80px !important;
          }
        }
      `}</style>

      {/* LEFT — copy + CTAs */}
      <div>
        <div style={{
          fontSize: 12, fontWeight: 600,
          letterSpacing: '0.28em',
          textTransform: 'uppercase',
          color: C.cyan,
          marginBottom: 32,
          lineHeight: 1,
        }}>
          Where retail meets intelligence
        </div>

        <h1 style={{
          fontSize: 'clamp(40px, 5vw, 60px)',
          fontWeight: 600,
          letterSpacing: '-0.035em',
          lineHeight: 1.02,
          color: C.textPrimary,
          marginBottom: 24,
          margin: '0 0 24px 0',
        }}>
          One permanent ID for every{' '}
          <span style={{
            color: C.cyan,
            textShadow: '0 0 40px rgba(0,212,255,0.4)',
          }}>retail outlet</span>.
        </h1>

        <p style={{
          fontSize: 18,
          color: C.textSecondary,
          maxWidth: 520,
          marginBottom: 40,
          lineHeight: 1.6,
          margin: '0 0 40px 0',
        }}>
          Meridian is the master registry for FMCG. We match, deduplicate, and enrich outlet data across every client, every submission, and every market — so your teams stop arguing about which shops exist and start selling to them.
        </p>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <PrimaryBtn onClick={() => navigate('/request-demo')}>Request a demo</PrimaryBtn>
          <ScrollBtn href="#product-tour">
            Tour the product
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <line x1={12} y1={5} x2={12} y2={19} />
              <polyline points="19 12 12 19 5 12" />
            </svg>
          </ScrollBtn>
        </div>
      </div>

      {/* RIGHT — product screenshot (client dashboard) */}
      <HeroMockup />
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════
 * HERO MOCKUP — fake browser chrome + app sidebar + client dashboard
 * Closely mirrors Pass 2 MatchResults.tsx client view
 * ═══════════════════════════════════════════════════════════════ */

function HeroMockup() {
  return (
    <div style={{
      position: 'relative',
      borderRadius: 14,
      overflow: 'hidden',
      background: C.bgBase,
      border: `1px solid ${C.borderDefault}`,
      boxShadow: '0 24px 64px -12px rgba(0,0,0,0.5), 0 0 0 1px rgba(0,212,255,0.08), 0 0 40px -10px rgba(0,212,255,0.15)',
    }}>
      {/* App grid: sidebar + main (matches the real app layout) */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '160px 1fr',
      }}>
        <MockSidebar />
        <MockMain />
      </div>
    </div>
  );
}

function MockSidebar() {
  return (
    <aside style={{
      background: C.bgPanel,
      borderRight: `1px solid ${C.borderSubtle}`,
      padding: '14px 0 14px',
      display: 'flex', flexDirection: 'column', gap: 14,
    }}>
      {/* Brand — matches real sidebar: full wordmark logo, "where retail meets intelligence" eyebrow below */}
      <div style={{
        padding: '0 10px',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
      }}>
        <img
          alt="Meridian Registry"
          src="/logo-transparent.png"
          style={{ width: '100%', maxWidth: 110, height: 'auto', display: 'block' }}
        />
        <div style={{
          fontSize: 6, fontWeight: 600,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          lineHeight: 1,
          marginTop: -2,
          textAlign: 'center',
          whiteSpace: 'nowrap',
        }}>
          <span style={{ color: C.textTertiary }}>Where retail meets </span>
          <span style={{ color: C.cyan }}>intelligence</span>
        </div>
      </div>

      {/* Workspace nav — mirrors real app's 7-item workspace group */}
      <div>
        <div style={{
          padding: '0 14px',
          fontSize: 7, fontWeight: 600,
          letterSpacing: '0.14em', textTransform: 'uppercase',
          color: C.textQuaternary,
          marginBottom: 4,
        }}>Workspace</div>
        <MockNavItem label="Dashboard" active icon={
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
            <rect x={3} y={3} width={7} height={9} />
            <rect x={14} y={3} width={7} height={5} />
            <rect x={14} y={12} width={7} height={9} />
            <rect x={3} y={16} width={7} height={5} />
          </svg>
        } />
        <MockNavItem label="My Records" icon={
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
            <line x1={8} y1={6} x2={21} y2={6} />
            <line x1={8} y1={12} x2={21} y2={12} />
            <line x1={8} y1={18} x2={21} y2={18} />
            <line x1={3} y1={6} x2={3.01} y2={6} />
            <line x1={3} y1={12} x2={3.01} y2={12} />
            <line x1={3} y1={18} x2={3.01} y2={18} />
          </svg>
        } />
        <MockNavItem label="Matched Outlets" icon={
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 6 9 17l-5-5" />
          </svg>
        } />
        <MockNavItem label="Review Queue" badge="253" icon={
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
            <circle cx={12} cy={12} r={9} />
            <path d="M12 7v5l3 2" />
          </svg>
        } />
        <MockNavItem label="Distribution Gaps" icon={
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 3v18h18" />
            <path d="m7 17 4-8 4 4 6-10" />
          </svg>
        } />
        <MockNavItem label="Market Intelligence" icon={
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
            <path d="M2 12h3m14 0h3M12 2v3m0 14v3" />
            <circle cx={12} cy={12} r={4} />
          </svg>
        } />
        <MockNavItem label="Registry Map" icon={
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2C8 2 5 5 5 9c0 5 7 13 7 13s7-8 7-13c0-4-3-7-7-7z" />
            <circle cx={12} cy={9} r={2.5} />
          </svg>
        } />
        <MockNavItem label="Reports" icon={
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1={8} y1={13} x2={16} y2={13} />
            <line x1={8} y1={17} x2={16} y2={17} />
          </svg>
        } />
      </div>
    </aside>
  );
}

function MockNavItem({ label, icon, active, badge }: {
  label: string;
  icon: React.ReactNode;
  active?: boolean;
  badge?: string;
}) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 7,
      padding: '5px 14px',
      fontSize: 8.5, fontWeight: active ? 600 : 500,
      color: active ? C.textPrimary : C.textSecondary,
      borderLeft: `2px solid ${active ? C.cyan : 'transparent'}`,
      background: active ? 'linear-gradient(90deg, rgba(0,212,255,0.10), transparent 70%)' : 'transparent',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }}>
      <div style={{
        width: 10, height: 10,
        color: active ? C.cyan : C.textTertiary,
        flexShrink: 0,
      }}>
        {icon}
      </div>
      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
      {badge && (
        <span style={{
          fontSize: 7,
          fontFamily: F.data,
          fontWeight: 600,
          color: C.warning,
          background: 'rgba(245, 158, 11, 0.10)',
          border: '1px solid rgba(245, 158, 11, 0.30)',
          padding: '0px 4px',
          borderRadius: 3,
          flexShrink: 0,
          lineHeight: 1.4,
        }}>{badge}</span>
      )}
    </div>
  );
}

function MockMain() {
  return (
    <div style={{
      padding: '14px 18px',
      background: C.bgBase,
      minWidth: 0,
      // Flex column with content pinned top; the MockMain div stretches to
      // match sidebar's height via grid align-items:stretch. Donut is the
      // last child, so its bottom edge sits at MockMain's bottom edge
      // (minus 14px padding), which matches where the sidebar's last nav
      // item ends. This produces the clean bottom alignment seen in the
      // real app.
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Topbar — mirrors real dashboard header: "Dashboard", live status, company badge */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Dashboard</div>
          <div style={{
            fontSize: 8, color: C.textTertiary,
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <span style={{
                width: 4, height: 4, borderRadius: '50%',
                background: C.success, boxShadow: `0 0 4px ${C.success}`,
              }} />
              Live registry
            </span>
            <span style={{ color: C.textQuaternary }}>·</span>
            <span>synced 2m ago</span>
            <span style={{ color: C.textQuaternary }}>·</span>
            <span style={{
              padding: '1px 6px',
              border: `1px solid ${C.cyan}44`,
              borderRadius: 3,
              background: C.cyanSoft,
              color: C.cyanLight,
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
            }}>Acme Beverages</span>
          </div>
        </div>
      </div>

      {/* KPI strip — mirrors the real client dashboard layout:
         Match rate · My records · Matched outlets · Unmatched · Pending review */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1.3fr 1fr 1fr 1fr 1fr',
        gap: 1,
        background: C.borderSubtle,
        borderRadius: 8,
        overflow: 'hidden',
        border: `1px solid ${C.borderDefault}`,
        marginBottom: 14,
      }}>
        <MockKPI primary label="Match rate" value="96.4" unit="%" delta="+1.8 pts" deltaType="up" />
        <MockKPI label="My records" value="8,412" delta="+124" deltaType="up" />
        <MockKPI label="Matched outlets" value="8,110" delta="+158" deltaType="up" />
        <MockKPI label="Unmatched" value="49" delta="-18" deltaType="up" />
        <MockKPI label="Pending review" value="253" delta="+16" deltaType="down" />
      </div>

      {/* Flex spacer — absorbs any extra height from grid stretching.
          Without this, the sidebar's taller content forces MockMain to
          stretch, and the leftover space appears below the donut card,
          breaking bottom alignment with the sidebar's last nav item. */}
      <div style={{ flex: 1, minHeight: 0 }} />

      {/* Match rate hero — ring + breakdown */}
      <MockMatchHero />
    </div>
  );
}

function MockKPI({
  primary, label, value, unit, delta, deltaType,
}: {
  primary?: boolean;
  label: string;
  value: string;
  unit?: string;
  delta: string;
  deltaType: 'up' | 'down' | 'neutral';
}) {
  const deltaColor =
    deltaType === 'up' ? C.success :
    deltaType === 'down' ? C.danger :
    C.textTertiary;
  return (
    <div style={{
      background: primary
        ? `linear-gradient(135deg, ${C.bgPanel}, rgba(0,212,255,0.04))`
        : C.bgPanel,
      padding: '8px 10px',
      position: 'relative',
      display: 'flex', flexDirection: 'column', gap: 3,
    }}>
      {primary && (
        <div aria-hidden style={{
          position: 'absolute', inset: 0,
          borderLeft: `2px solid ${C.cyan}`,
          boxShadow: `inset 0 0 30px -8px ${C.cyanGlow}`,
          pointerEvents: 'none',
        }} />
      )}
      <div style={{
        fontSize: 7, fontWeight: 600,
        letterSpacing: '0.1em', textTransform: 'uppercase',
        color: C.textTertiary,
      }}>{label}</div>
      <div style={{
        fontFamily: F.data, fontSize: 15, fontWeight: 700,
        color: primary ? C.cyanLight : C.textPrimary,
        lineHeight: 1,
        fontVariantNumeric: 'tabular-nums',
      }}>
        {value}{unit && <span style={{ fontSize: 10, color: C.textTertiary }}>{unit}</span>}
      </div>
      <div style={{ fontFamily: F.data, fontSize: 7, color: deltaColor }}>{delta}</div>
    </div>
  );
}

function MockMatchHero() {
  // Ring geometry — 90×90 card, radius 36, circumference 2π × 36 ≈ 226.19.
  // Segment lengths for 96.4% / 3.0% / 0.6%:
  const C_LEN = 226.19;
  const MATCHED_LEN   = C_LEN * 0.964;  // 218.05
  const PENDING_LEN   = C_LEN * 0.030;  //   6.79
  const UNMATCHED_LEN = C_LEN * 0.006;  //   1.36
  const PENDING_OFFSET   = -MATCHED_LEN;
  const UNMATCHED_OFFSET = -(MATCHED_LEN + PENDING_LEN);
  return (
    <div style={{
      background: `linear-gradient(135deg, ${C.bgPanel}, rgba(0,212,255,0.04))`,
      border: `1px solid rgba(0,212,255,0.3)`,
      borderRadius: 8,
      padding: 12,
      display: 'grid',
      gridTemplateColumns: '90px 1fr',
      gap: 14,
      alignItems: 'center',
      position: 'relative',
      overflow: 'hidden',
      boxShadow: '0 0 16px -4px rgba(0,212,255,0.2)',
    }}>
      <div aria-hidden style={{
        position: 'absolute',
        left: -60, top: -60, width: 200, height: 200,
        background: `radial-gradient(circle, ${C.cyanSoft} 0%, transparent 60%)`,
        pointerEvents: 'none',
      }} />

      {/* Ring */}
      <div style={{ position: 'relative', width: 90, height: 90, flexShrink: 0 }}>
        <svg width={90} height={90} viewBox="0 0 90 90" style={{
          transform: 'rotate(-90deg)',
          filter: `drop-shadow(0 0 6px ${C.cyanGlow})`,
        }}>
          <defs>
            <linearGradient id="ring-grad-hero" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={C.cyan} />
              <stop offset="100%" stopColor={C.cyanLight} />
            </linearGradient>
          </defs>
          {/* Gray track */}
          <circle cx={45} cy={45} r={36} fill="none" stroke="rgba(148,163,184,0.08)" strokeWidth={6} />
          {/* Matched (cyan) */}
          <circle cx={45} cy={45} r={36} fill="none" stroke="url(#ring-grad-hero)" strokeWidth={6}
            strokeLinecap="butt"
            strokeDasharray={`${MATCHED_LEN} ${C_LEN - MATCHED_LEN}`} />
          {/* Pending (amber) */}
          <circle cx={45} cy={45} r={36} fill="none" stroke={C.warning} strokeWidth={6}
            strokeLinecap="butt"
            strokeDasharray={`${PENDING_LEN} ${C_LEN - PENDING_LEN}`}
            strokeDashoffset={PENDING_OFFSET} />
          {/* Unmatched (red) */}
          <circle cx={45} cy={45} r={36} fill="none" stroke={C.danger} strokeWidth={6}
            strokeLinecap="butt"
            strokeDasharray={`${UNMATCHED_LEN} ${C_LEN - UNMATCHED_LEN}`}
            strokeDashoffset={UNMATCHED_OFFSET} />
        </svg>
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            fontFamily: F.data, fontSize: 18, fontWeight: 700,
            color: C.textPrimary, lineHeight: 1,
            fontVariantNumeric: 'tabular-nums',
          }}>
            96.4<span style={{ fontSize: 10, color: C.textTertiary }}>%</span>
          </div>
          <div style={{
            fontSize: 6, fontWeight: 600,
            letterSpacing: '0.18em', textTransform: 'uppercase',
            color: C.textTertiary, marginTop: 3,
            whiteSpace: 'nowrap',
          }}>Match rate</div>
        </div>
      </div>

      {/* Breakdown — mirrors real dashboard: Successfully matched / Pending review / Unmatched */}
      <div style={{ position: 'relative' }}>
        <div style={{ fontSize: 9, fontWeight: 600, marginBottom: 5 }}>Match quality breakdown</div>
        <BreakdownRow color={C.cyan} glow label="Successfully matched" value="8,110" pct="96.4%" />
        <BreakdownRow color={C.warning} label="Pending review" value="253" pct="3.0%" />
        <BreakdownRow color={C.danger} label="Unmatched" value="49" pct="0.6%" />
      </div>
    </div>
  );
}

function BreakdownRow({ color, glow, label, value, pct }: { color: string; glow?: boolean; label: string; value: string; pct: string }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '6px 1fr auto auto',
      gap: 6, alignItems: 'center',
      padding: '3px 0',
      borderBottom: `1px solid ${C.borderSubtle}`,
    }}>
      <span style={{
        width: 5, height: 5, borderRadius: 1,
        background: color,
        boxShadow: glow ? `0 0 4px ${C.cyanGlow}` : undefined,
      }} />
      <span style={{ fontSize: 8, color: C.textSecondary }}>{label}</span>
      <span style={{ fontFamily: F.data, fontSize: 8, color: C.textPrimary, fontWeight: 600 }}>{value}</span>
      <span style={{ fontFamily: F.data, fontSize: 7, color: C.textTertiary, minWidth: 26, textAlign: 'right' }}>{pct}</span>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
 * PROBLEM — long-form text block
 * ═══════════════════════════════════════════════════════════════ */

function Problem() {
  return (
    <section style={{
      maxWidth: 1280, margin: '0 auto',
      padding: '120px 32px 60px',
    }}>
      <div style={{ maxWidth: 880 }}>
        <div style={{
          fontSize: 11, fontWeight: 600,
          letterSpacing: '0.22em', textTransform: 'uppercase',
          color: C.cyan, marginBottom: 16,
        }}>The problem</div>
        <h2 style={{
          fontSize: 'clamp(28px, 4vw, 40px)',
          fontWeight: 600, letterSpacing: '-0.025em',
          lineHeight: 1.18,
          color: C.textPrimary,
          margin: '0 0 24px 0',
        }}>
          You're leaving revenue on the table. You just can't see how much.
        </h2>
        <div style={{
          display: 'flex', flexDirection: 'column', gap: 20,
          fontSize: 18, color: C.textSecondary,
          lineHeight: 1.7,
        }}>
          <p style={{ margin: 0 }}>
            Your brand. Your distributor. Your third-party data provider. All three maintain lists of the same physical stores — with different names, different addresses, no shared identity.
          </p>
          <p style={{ margin: 0 }}>
            <strong style={{ color: C.textPrimary, fontWeight: 500 }}>"Westside Market · 412 Main St."</strong>{' '}
            <strong style={{ color: C.textPrimary, fontWeight: 500 }}>"WESTSIDE MARKET LLC · 412 Main Street #B."</strong>{' '}
            <strong style={{ color: C.textPrimary, fontWeight: 500 }}>"West Side Grocery."</strong>{' '}
            Three records. One store. Zero clarity.
          </p>
          <p style={{ margin: 0 }}>
            Every outlet your brand doesn't reach is revenue that went elsewhere. Every gap in your distribution has a value — and until now, calculating it required weeks of analyst work that was stale before it was finished.
          </p>
          <p style={{ margin: 0, color: C.textPrimary }}>
            Meridian gives every retail location a permanent identity. Gaps become visible. Opportunities become quantifiable. Your sales team stops guessing.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════
 * SOLUTION 3-UP
 * ═══════════════════════════════════════════════════════════════ */

function Solution() {
  return (
    <section style={{
      maxWidth: 1280, margin: '0 auto',
      padding: '40px 32px 120px',
    }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: 20,
      }}>
        <SolutionCard
          title="Permanent Census IDs"
          desc="Every physical location gets a permanent ID the moment it enters the registry. It never changes, even as names, addresses, and owners do."
          icon={
            <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
              <ellipse cx={12} cy={5} rx={9} ry={3} />
              <path d="M3 5v14c0 1.7 4 3 9 3s9-1.3 9-3V5" />
              <path d="M3 12c0 1.7 4 3 9 3s9-1.3 9-3" />
            </svg>
          }
        />
        <SolutionCard
          title="Three-layer matching"
          desc="Tax ID, AI name and address similarity, and GPS proximity — combined, they identify the same outlet across submissions automatically. Humans only review the edge cases."
          icon={
            <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 6 9 17l-5-5" />
            </svg>
          }
        />
        <SolutionCard
          title="Same-day onboarding"
          desc="Upload your outlet list today, see it matched against the registry the same day. No integration project, no multi-month onboarding, no consulting engagement."
          icon={
            <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 3v18h18" />
              <path d="m7 17 4-8 4 4 6-10" />
            </svg>
          }
        />
      </div>
    </section>
  );
}

function SolutionCard({ title, desc, icon }: { title: string; desc: string; icon: React.ReactNode }) {
  return (
    <div
      onMouseEnter={e => { e.currentTarget.style.borderColor = C.borderStrong; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = C.borderDefault; e.currentTarget.style.transform = 'translateY(0)'; }}
      style={{
        background: C.bgPanel,
        border: `1px solid ${C.borderDefault}`,
        borderRadius: 14,
        padding: '32px 28px',
        transition: 'border-color 200ms ease, transform 200ms ease',
      }}>
      <div style={{
        width: 40, height: 40,
        borderRadius: 10,
        background: C.cyanSoft,
        border: '1px solid rgba(0,212,255,0.25)',
        color: C.cyan,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        marginBottom: 20,
      }}>{icon}</div>
      <div style={{
        fontSize: 17, fontWeight: 600,
        marginBottom: 10, color: C.textPrimary,
        letterSpacing: '-0.01em',
      }}>{title}</div>
      <div style={{ fontSize: 14, color: C.textSecondary, lineHeight: 1.65 }}>{desc}</div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
 * PRODUCT TOUR — 4 tabs with in-app mockups
 * ═══════════════════════════════════════════════════════════════ */

type TabKey = 'matched' | 'gaps' | 'atlas' | 'temporal';

function ProductTour() {
  const [tab, setTab] = useState<TabKey>('matched');

  const tabs: { key: TabKey; label: string }[] = [
    { key: 'matched', label: 'Matched outlets' },
    { key: 'gaps', label: 'Distribution gaps' },
    { key: 'atlas', label: 'Ask Atlas' },
    { key: 'temporal', label: 'Market intelligence' },
  ];

  return (
    <section id="product-tour" style={{
      maxWidth: 1280, margin: '0 auto',
      padding: '80px 32px 120px',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        textAlign: 'center', marginBottom: 48, gap: 12,
      }}>
        <div style={{
          fontSize: 11, fontWeight: 600,
          letterSpacing: '0.22em', textTransform: 'uppercase',
          color: C.cyan,
        }}>Product tour</div>
        <h2 style={{
          fontSize: 'clamp(28px, 4vw, 40px)',
          fontWeight: 600, letterSpacing: '-0.025em',
          lineHeight: 1.15, maxWidth: 720, margin: 0,
          color: C.textPrimary,
        }}>See what your team does with a single source of truth</h2>
        <p style={{
          fontSize: 16, color: C.textSecondary,
          maxWidth: 600, lineHeight: 1.6, margin: 0,
        }}>Four views into what your retail landscape actually looks like — and what you can do with it.</p>
      </div>

      {/* Tab switcher */}
      <div style={{
        display: 'flex', gap: 4, justifyContent: 'center',
        flexWrap: 'wrap',
        background: C.bgPanel,
        border: `1px solid ${C.borderDefault}`,
        borderRadius: 10,
        padding: 4,
        margin: '0 auto 32px',
        width: 'fit-content',
      }}>
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: '8px 16px',
              fontSize: 12,
              fontWeight: tab === t.key ? 600 : 500,
              fontFamily: F.ui,
              color: tab === t.key ? C.textPrimary : C.textSecondary,
              background: tab === t.key ? C.bgCard : 'transparent',
              border: 'none',
              borderRadius: 7,
              cursor: 'pointer',
              transition: 'all 120ms ease',
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Active panel */}
      {tab === 'matched' && <TourPanelMatched />}
      {tab === 'gaps' && <TourPanelGaps />}
      {tab === 'atlas' && <TourPanelAtlas />}
      {tab === 'temporal' && <TourPanelTemporal />}
    </section>
  );
}

/** Outer wrapper used by every tour panel. Left = copy, right = mockup. */
function TourPanel({ title, desc, bullets, mockup }: {
  title: string;
  desc: string;
  bullets: string[];
  mockup: React.ReactNode;
}) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr',
      gap: 40, alignItems: 'center',
      background: C.bgPanel,
      border: `1px solid ${C.borderDefault}`,
      borderRadius: 16,
      padding: 40,
    }}
    className="meridian-tour-panel">
      <style>{`
        @media (min-width: 900px) {
          .meridian-tour-panel {
            grid-template-columns: 1fr 1.4fr !important;
            padding: 48px !important;
            gap: 60px !important;
          }
        }
      `}</style>

      <div>
        <div style={{
          fontSize: 24, fontWeight: 600,
          letterSpacing: '-0.02em',
          marginBottom: 12,
          color: C.textPrimary,
        }}>{title}</div>
        <div style={{
          fontSize: 15, color: C.textSecondary,
          lineHeight: 1.65, marginBottom: 20,
        }}>{desc}</div>
        <ul style={{
          listStyle: 'none', padding: 0, margin: 0,
          display: 'flex', flexDirection: 'column', gap: 10,
        }}>
          {bullets.map((b, i) => (
            <li key={i} style={{
              display: 'flex', gap: 10, alignItems: 'flex-start',
              fontSize: 13, color: C.textSecondary, lineHeight: 1.5,
            }}>
              <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={C.cyan} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}>
                <path d="M20 6 9 17l-5-5" />
              </svg>
              {b}
            </li>
          ))}
        </ul>
      </div>

      {mockup}
    </div>
  );
}

/** Shared chrome for tour mockups. */
function TourMockup({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      background: C.bgBase,
      borderRadius: 10,
      border: `1px solid ${C.borderDefault}`,
      overflow: 'hidden',
      minHeight: 320,
      display: 'flex', flexDirection: 'column',
    }}>
      <div style={{ padding: 16, flex: 1 }}>{children}</div>
    </div>
  );
}

/* ──────── Tour Panel 1 — Matched Outlets ──────── */

function TourPanelMatched() {
  return (
    <TourPanel
      title="Every match, every method, every confidence score."
      desc="Every row in your submission lives here, linked to the canonical registry. Filter by client, city, outlet format, or match method. Click any Census ID to see the full outlet history."
      bullets={[
        'Match method and confidence visible at a glance',
        'One-click export to Excel, or pull via REST API',
        'Audit-ready — every match has a full lineage',
      ]}
      mockup={
        <TourMockup>
          <MiniTable
            columns={['Census ID', 'Outlet', 'Method', 'Conf.']}
            colWidths={['80px', '1fr', '80px', '60px']}
            rightAlignLast
            rows={[
              ['MRN-AB4921', `Momma's Bakery`, 'EXACT', '100%'],
              ['MRN-CD7103', 'Eastside Deli', 'EXACT', '100%'],
              ['MRN-EF3388', 'Harbor Mini Mart', 'SMART', '96%'],
              ['MRN-GH2156', 'Sunrise Convenience', 'EXACT', '100%'],
              ['MRN-IJ5847', 'Corner Pharmacy #4', 'SMART', '78%'],
              ['MRN-KL9012', 'Lakeview Grocery', 'EXACT', '100%'],
              ['MRN-MN4483', 'Northside Market', 'EXACT', '100%'],
              ['MRN-OP7621', 'Midway Fuel & Food', 'SMART', '93%'],
              ['MRN-QR3309', 'Hilltop Provisions', 'EXACT', '100%'],
              ['MRN-ST8845', 'Willow Creek Grocer', 'SMART', '87%'],
              ['MRN-UV2177', 'River Street Market', 'EXACT', '100%'],
              ['MRN-WX6034', 'Bayview Kiosk', 'SMART', '72%'],
            ]}
            mode="matched"
          />
        </TourMockup>
      }
    />
  );
}

/* ──────── Tour Panel 2 — Distribution Gaps ──────── */

function TourPanelGaps() {
  return (
    <TourPanel
      title="Find the outlets your competitors cover that you don't."
      desc="Pick a client, a category, and a target market share. Meridian surfaces every outlet that's in the registry but missing from your distribution — with a volume estimate for each."
      bullets={[
        'Total opportunity value, computed in EUR or USD',
        'Filter by geography, outlet type, and category',
        'Hand directly to field sales as a prioritized hit list',
      ]}
      mockup={
        <TourMockup>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 8, marginBottom: 12,
          }}>
            <MiniStat label="Gap outlets" value="1,247" />
            <MiniStat label="Opportunity" value="$4.2M" />
            <MiniStat label="Avg / outlet" value="$3.4K" />
          </div>
          <MiniTable
            columns={['Outlet', 'City', 'Value', 'Conf.']}
            colWidths={['1fr', '1fr', '80px', '60px']}
            rightAlignLast
            rows={[
              ['Midtown Market', 'New York City', '$2,040', 'Med'],
              ['Lakeshore Foods', 'Chicago', '$3,360', 'Med'],
              ['Bay Area Grocery', 'San Francisco', '$4,800', 'High'],
              ['Sunset Convenience', 'Los Angeles', '$3,360', 'High'],
              ['Harbor Mini Mart', 'Seattle', '$2,890', 'Med'],
              ['Summit Provisions', 'Denver', '$4,120', 'High'],
              ['Riverside Market', 'Austin', '$2,450', 'Med'],
              ['Ashford Grocery', 'Boston', '$3,780', 'High'],
              ['Parkside Corner', 'Miami', '$1,920', 'Med'],
            ]}
            mode="gaps"
          />
        </TourMockup>
      }
    />
  );
}

/* ──────── Tour Panel 3 — Atlas ──────── */

function TourPanelAtlas() {
  return (
    <TourPanel
      title="Ask Atlas — your built-in AI analyst."
      desc="No SQL. No dashboards to build. Ask coverage questions in plain English and get data-backed answers in seconds, sourced directly from your registry."
      bullets={[
        'Grounded in your actual registry data',
        'Understands match methods, confidence, and cycles',
        'Every answer is linkable and exportable',
      ]}
      mockup={
        <TourMockup>
          <div style={{
            background: C.bgPanel,
            border: `1px solid ${C.borderDefault}`,
            borderRadius: 8,
            padding: 12,
            display: 'flex', flexDirection: 'column', gap: 8,
            minHeight: 260,
          }}>
            <ChatBubble kind="user">What are my top distribution gaps in Chicago?</ChatBubble>
            <ChatBubble kind="assistant">
              You have <Strong>84 gap outlets</Strong> in Chicago with a total opportunity of <Strong>$127K annually</Strong>. The top five by estimated value are Bay Area Grocery, Sunset Convenience, Lakeshore Foods, Midtown Market, and Harbor Mini Mart. Want me to export the full list to Excel?
            </ChatBubble>
            <ChatBubble kind="user">How many new outlets appeared last cycle?</ChatBubble>
            <ChatBubble kind="assistant">
              <Strong>200 new outlets</Strong> were added in the latest cycle. <Strong>32</Strong> of those are in your current coverage footprint and <Strong>168</Strong> are outside it. The largest new cluster is in the Pacific Northwest.
            </ChatBubble>
            <div style={{
              marginTop: 'auto',
              background: C.bgCard,
              border: `1px solid ${C.borderDefault}`,
              borderRadius: 6,
              padding: '6px 10px',
              fontSize: 9, color: C.textQuaternary,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <span>Ask Atlas anything…</span>
              <span>⌘K</span>
            </div>
          </div>
        </TourMockup>
      }
    />
  );
}

function ChatBubble({ kind, children }: { kind: 'user' | 'assistant'; children: React.ReactNode }) {
  const isUser = kind === 'user';
  return (
    <div style={{
      padding: '7px 10px',
      borderRadius: isUser ? '8px 8px 2px 8px' : '2px 8px 8px 8px',
      fontSize: 10, lineHeight: 1.5,
      maxWidth: '85%',
      background: isUser ? C.cyanSoft : 'rgba(255,255,255,0.04)',
      border: isUser ? `1px solid rgba(0,212,255,0.3)` : 'none',
      color: isUser ? C.textPrimary : C.textSecondary,
      alignSelf: isUser ? 'flex-end' : 'flex-start',
    }}>{children}</div>
  );
}

function Strong({ children }: { children: React.ReactNode }) {
  return <strong style={{ color: C.textPrimary, fontWeight: 600 }}>{children}</strong>;
}

/* ──────── Tour Panel 4 — Market Intelligence ──────── */

function TourPanelTemporal() {
  return (
    <TourPanel
      title="Track how the retail landscape changes over time."
      desc="Each data cycle is a snapshot. Compare any two cycles to see new outlets, probable closures, and coverage shifts — automatically, without rebuilding reports."
      bullets={[
        'New openings detected automatically',
        'Probable closures surfaced by sighting patterns',
        'Drill into any change back to the source records',
      ]}
      mockup={
        <TourMockup>
          <div style={{
            display: 'flex', gap: 8, marginBottom: 10,
            fontSize: 9, color: C.textSecondary,
            alignItems: 'center',
          }}>
            <span>Compare</span>
            <span style={pillStyle}>Current cycle</span>
            <span>vs</span>
            <span style={pillStyle}>Previous cycle</span>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 6, marginBottom: 12,
          }}>
            <CycleStat label="New outlets" value="200" tone="success" />
            <CycleStat label="Disappeared" value="8" tone="danger" />
            <CycleStat label="Likely closures" value="3" tone="warning" />
            <CycleStat label="Net change" value="+192" tone="cyan" />
          </div>
          <MiniTable
            columns={['Census ID', 'Outlet', 'City', 'Event']}
            colWidths={['80px', '1fr', '1fr', '60px']}
            rightAlignLast
            rows={[
              ['MRN-MN2847', 'Riverside Quick Shop', 'Portland', 'NEW'],
              ['MRN-OP6391', 'Golden Gate Market', 'San Francisco', 'NEW'],
              ['MRN-QR1503', 'Sunset Mini Mart', 'Phoenix', 'NEW'],
              ['MRN-ST7728', 'Maple Avenue Grocer', 'Minneapolis', 'NEW'],
              ['MRN-UV4492', 'Harborfront Deli', 'Baltimore', 'CLOSED'],
              ['MRN-WX8106', 'Lakeside Corner Store', 'Cleveland', 'NEW'],
              ['MRN-YZ2215', 'Old Town Provisions', 'Santa Fe', 'CLOSED'],
            ]}
            mode="temporal"
          />
        </TourMockup>
      }
    />
  );
}

const pillStyle: React.CSSProperties = {
  padding: '3px 8px', borderRadius: 4,
  background: C.bgCard,
  border: `1px solid ${C.borderDefault}`,
  fontFamily: F.data,
  fontSize: 9,
};

function CycleStat({ label, value, tone }: { label: string; value: string; tone: 'success' | 'danger' | 'warning' | 'cyan' }) {
  const borderColor =
    tone === 'success' ? C.success :
    tone === 'danger' ? C.danger :
    tone === 'warning' ? C.warning :
    C.cyan;
  return (
    <div style={{
      background: C.bgPanel,
      border: `1px solid ${C.borderDefault}`,
      borderLeft: `2px solid ${borderColor}`,
      borderRadius: 6,
      padding: 8,
    }}>
      <div style={{
        fontSize: 6, color: C.textTertiary,
        textTransform: 'uppercase', letterSpacing: '0.1em',
        marginBottom: 3, fontWeight: 600,
      }}>{label}</div>
      <div style={{ fontFamily: F.data, fontSize: 13, fontWeight: 700, color: C.textPrimary }}>{value}</div>
    </div>
  );
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div style={{
      background: C.bgPanel,
      border: `1px solid ${C.borderDefault}`,
      borderRadius: 6, padding: '8px 10px',
    }}>
      <div style={{
        fontSize: 7, color: C.textTertiary,
        textTransform: 'uppercase', letterSpacing: '0.1em',
        marginBottom: 4, fontWeight: 600,
      }}>{label}</div>
      <div style={{ fontFamily: F.data, fontSize: 16, fontWeight: 700, color: C.cyanLight }}>{value}</div>
    </div>
  );
}

/**
 * Tiny in-mockup table. Mode controls how the last few columns render
 * (method pills, confidence colors, event badges).
 */
function MiniTable({
  columns, colWidths, rows, mode, rightAlignLast,
}: {
  columns: string[];
  colWidths: string[];
  rows: string[][];
  mode: 'matched' | 'gaps' | 'temporal';
  rightAlignLast?: boolean;
}) {
  const grid = colWidths.join(' ');

  return (
    <div style={{
      border: `1px solid ${C.borderDefault}`,
      borderRadius: 6, overflow: 'hidden',
      background: C.bgPanel,
    }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: grid,
        gap: 8, padding: '6px 10px',
        background: C.bgCard,
        fontSize: 8, fontWeight: 600,
        letterSpacing: '0.08em', textTransform: 'uppercase',
        color: C.textTertiary,
        borderBottom: `1px solid ${C.borderDefault}`,
      }}>
        {columns.map((c, i) => (
          <div key={i} style={{ textAlign: rightAlignLast && i === columns.length - 1 ? 'right' : 'left' }}>{c}</div>
        ))}
      </div>
      {rows.map((row, ri) => (
        <div key={ri} style={{
          display: 'grid',
          gridTemplateColumns: grid,
          gap: 8, padding: '7px 10px',
          borderBottom: ri === rows.length - 1 ? 'none' : `1px solid ${C.borderSubtle}`,
          background: ri % 2 === 1 ? 'rgba(15, 22, 40, 0.4)' : 'transparent',
          fontSize: 10, alignItems: 'center',
        }}>
          {row.map((cell, ci) => (
            <MiniTableCell
              key={ci}
              value={cell}
              colIndex={ci}
              lastIndex={row.length - 1}
              mode={mode}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

function MiniTableCell({ value, colIndex, lastIndex, mode }: {
  value: string;
  colIndex: number;
  lastIndex: number;
  mode: 'matched' | 'gaps' | 'temporal';
}) {
  // First column — Census ID (matched, temporal) or outlet name (gaps)
  if (colIndex === 0 && (mode === 'matched' || mode === 'temporal')) {
    return <span style={{ color: C.cyanLight, fontFamily: F.data, fontSize: 9 }}>{value}</span>;
  }
  // Method column in matched mode (column 2)
  if (mode === 'matched' && colIndex === 2) {
    const isExact = value === 'EXACT';
    return (
      <span>
        <span style={{
          display: 'inline-block',
          padding: '1px 5px', borderRadius: 3,
          fontFamily: F.data, fontSize: 7, fontWeight: 700,
          letterSpacing: '0.05em',
          background: isExact ? 'rgba(16,185,129,0.1)' : C.cyanSoft,
          color: isExact ? C.success : C.cyanLight,
          border: `1px solid ${isExact ? 'rgba(16,185,129,0.3)' : 'rgba(0,212,255,0.3)'}`,
        }}>{value}</span>
      </span>
    );
  }
  // Last column — confidence (matched, gaps) or NEW badge (temporal)
  if (colIndex === lastIndex) {
    if (mode === 'temporal' && value === 'NEW') {
      return <span style={{ fontFamily: F.data, fontSize: 9, fontWeight: 700, color: C.success, textAlign: 'right' }}>{value}</span>;
    }
    const isHigh = value === 'High' || (value.endsWith('%') && parseInt(value) >= 90);
    return (
      <span style={{
        fontFamily: F.data, fontSize: 9, fontWeight: 700,
        textAlign: 'right',
        color: isHigh ? C.success : C.warning,
      }}>{value}</span>
    );
  }
  // Value column in gaps mode (column 2)
  if (mode === 'gaps' && colIndex === 2) {
    return <span style={{ color: C.cyanLight, fontFamily: F.data, fontSize: 9 }}>{value}</span>;
  }
  // Default
  return <span style={{ color: C.textSecondary }}>{value}</span>;
}

/* ═══════════════════════════════════════════════════════════════
 * FEATURES GRID — 6 cards
 * ═══════════════════════════════════════════════════════════════ */

function Features() {
  return (
    <section id="features" style={{
      maxWidth: 1280, margin: '0 auto',
      padding: '80px 32px 120px',
    }}>
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        textAlign: 'center', gap: 12, marginBottom: 56,
      }}>
        <div style={{
          fontSize: 11, fontWeight: 600,
          letterSpacing: '0.22em', textTransform: 'uppercase',
          color: C.cyan,
        }}>What's included</div>
        <h2 style={{
          fontSize: 'clamp(28px, 4vw, 40px)',
          fontWeight: 600, letterSpacing: '-0.025em',
          lineHeight: 1.15, maxWidth: 680, margin: 0,
          color: C.textPrimary,
        }}>Everything you need to run your retail data as infrastructure, not a spreadsheet</h2>
        <p style={{
          fontSize: 16, color: C.textSecondary,
          maxWidth: 560, lineHeight: 1.6, margin: 0,
        }}>Built for teams that need outlet data to be a source of truth — not a project that starts over every quarter.</p>
      </div>
      <div
        className="meridian-feature-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: 20,
        }}>
        <style>{`
          @media (min-width: 680px)  { .meridian-feature-grid { grid-template-columns: 1fr 1fr !important; } }
          @media (min-width: 1000px) { .meridian-feature-grid { grid-template-columns: 1fr 1fr 1fr !important; } }
        `}</style>
        {FEATURES.map(f => (
          <FeatureCard key={f.title} {...f} />
        ))}
      </div>
    </section>
  );
}

const FEATURES = [
  {
    title: 'One ID Per Outlet',
    desc: 'Every physical location gets a permanent Census ID the moment it enters the registry. Names, addresses, and owners can change — the ID stays constant. A single source of truth across your analytics, CRM, and ERP.',
    icon: (
      <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
        <ellipse cx={12} cy={5} rx={9} ry={3} />
        <path d="M3 5v14c0 1.7 4 3 9 3s9-1.3 9-3V5" />
        <path d="M3 12c0 1.7 4 3 9 3s9-1.3 9-3" />
      </svg>
    ),
  },
  {
    title: 'Automated Outlet Recognition',
    desc: 'Three-layer matching — exact tax ID, AI-powered name and address similarity, and GPS proximity — identifies the same outlet across submissions automatically. Match rates consistently exceed 95% on clean data.',
    icon: (
      <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
        <circle cx={11} cy={11} r={8} />
        <path d="m21 21-4.35-4.35" />
      </svg>
    ),
  },
  {
    title: 'Distribution Gaps',
    desc: `See which outlets competitors cover that you don't. Filter by geography, outlet type, and channel to surface the highest-value gaps and prioritize field sales activity.`,
    icon: (
      <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 3v18h18" />
        <path d="m7 17 4-8 4 4 6-10" />
      </svg>
    ),
  },
  {
    title: 'Market Intelligence',
    desc: 'Track outlet-level changes across submission cycles. New openings, closures, and coverage shifts surface automatically — giving sales and category teams a continuously updated view of the retail landscape.',
    icon: (
      <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 12h3m14 0h3M12 2v3m0 14v3" />
        <circle cx={12} cy={12} r={4} />
      </svg>
    ),
  },
  {
    title: 'Ask Atlas Anything',
    desc: `Meridian's built-in AI analyst. Ask questions about your coverage, gaps, and market position in plain language and get instant, data-backed answers — no SQL, no dashboards, no waiting on a report.`,
    icon: (
      <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 3l1.88 4.66L18.5 8.5l-3.66 3.58L15.76 17 12 14.5 8.24 17l.92-4.92L5.5 8.5l4.62-.84z" />
      </svg>
    ),
  },
  {
    title: 'Seamless Integration',
    desc: 'Export matched data in standard formats or connect via API. Registry data flows into existing CRM, ERP, and sales planning systems without manual transformation.',
    icon: (
      <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
        <polyline points="15 3 21 3 21 9" />
        <line x1={10} y1={14} x2={21} y2={3} />
      </svg>
    ),
  },
];

function FeatureCard({ title, desc, icon }: { title: string; desc: string; icon: React.ReactNode }) {
  return (
    <div
      onMouseEnter={e => { e.currentTarget.style.borderColor = C.borderStrong; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = C.borderDefault; }}
      style={{
        background: C.bgPanel,
        border: `1px solid ${C.borderDefault}`,
        borderRadius: 14,
        padding: '32px 28px',
        transition: 'border-color 200ms ease',
      }}>
      <div style={{
        width: 40, height: 40,
        borderRadius: 10,
        background: C.cyanSoft,
        border: '1px solid rgba(0,212,255,0.25)',
        color: C.cyan,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        marginBottom: 20,
      }}>{icon}</div>
      <div style={{
        fontSize: 17, fontWeight: 600,
        marginBottom: 10, color: C.textPrimary,
        letterSpacing: '-0.01em',
      }}>{title}</div>
      <div style={{ fontSize: 14, color: C.textSecondary, lineHeight: 1.65 }}>{desc}</div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
 * FINAL CTA
 * ═══════════════════════════════════════════════════════════════ */

function FinalCTA({ navigate }: { navigate: (path: string) => void }) {
  return (
    <section style={{
      maxWidth: 1280, margin: '0 auto',
      padding: '120px 32px',
      textAlign: 'center',
    }}>
      <div style={{
        background: `linear-gradient(135deg, ${C.bgPanel} 0%, rgba(0,212,255,0.04) 100%)`,
        border: `1px solid ${C.borderDefault}`,
        borderRadius: 20,
        padding: '80px 40px',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 0 60px -20px rgba(0,212,255,0.3)',
      }}>
        <div aria-hidden style={{
          position: 'absolute',
          top: -100, left: '50%', transform: 'translateX(-50%)',
          width: 600, height: 400,
          background: `radial-gradient(ellipse at center, ${C.cyanSoft} 0%, transparent 60%)`,
          pointerEvents: 'none',
        }} />

        <div style={{
          fontSize: 11, fontWeight: 600,
          letterSpacing: '0.22em', textTransform: 'uppercase',
          color: C.cyan, marginBottom: 20,
          position: 'relative',
        }}>Ready when you are</div>

        <h2 style={{
          fontSize: 'clamp(32px, 5vw, 48px)',
          fontWeight: 600, letterSpacing: '-0.03em',
          lineHeight: 1.1, marginBottom: 20,
          position: 'relative',
          color: C.textPrimary,
          margin: '0 0 20px 0',
        }}>See your retail landscape as it actually is.</h2>

        <p style={{
          fontSize: 17, color: C.textSecondary,
          maxWidth: 540, margin: '0 auto 40px',
          lineHeight: 1.6, position: 'relative',
        }}>Thirty minutes. No pitch, no commitment.<br />See your coverage gaps before your competition does.</p>

        <div style={{
          display: 'flex', gap: 12, justifyContent: 'center',
          flexWrap: 'wrap', position: 'relative',
        }}>
          <PrimaryBtn onClick={() => navigate('/request-demo')}>Request a demo</PrimaryBtn>
        </div>

        <div style={{
          fontSize: 13, color: C.textTertiary,
          marginTop: 24, position: 'relative',
        }}>
          Or email us directly at{' '}
          <a href="mailto:admin@meridianregistry.com"
            style={{ color: C.cyanLight, textDecoration: 'none' }}
            onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
            onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}>
            admin@meridianregistry.com
          </a>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════
 * FOOTER
 * ═══════════════════════════════════════════════════════════════ */

function Footer() {
  const linkStyle: React.CSSProperties = {
    fontSize: 13, color: C.textTertiary,
    textDecoration: 'none',
    transition: 'color 120ms ease',
  };
  const onEnter = (e: React.MouseEvent<HTMLElement>) => { e.currentTarget.style.color = C.textPrimary; };
  const onLeave = (e: React.MouseEvent<HTMLElement>) => { e.currentTarget.style.color = C.textTertiary; };

  return (
    <footer style={{
      borderTop: `1px solid ${C.borderSubtle}`,
      padding: '64px 32px 32px',
      background: 'rgba(11, 15, 31, 0.4)',
    }}>
      <div style={{
        maxWidth: 1280, margin: '0 auto',
        display: 'grid',
        // 3 equal columns so brand, Platform, and Legal are evenly spaced.
        // Below 720px each column stacks.
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: 48,
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <img alt="Meridian" src="/Meridian_M_logo-transparent.png" style={{ height: 28 }} />
            <span style={{
              fontSize: 14, fontWeight: 700,
              letterSpacing: '0.22em',
              color: C.textPrimary,
              textTransform: 'uppercase',
            }}>Meridian</span>
          </div>
          <div style={{
            fontSize: 11, fontWeight: 600,
            letterSpacing: '0.2em', textTransform: 'uppercase',
            color: C.textTertiary,
            marginBottom: 24,
          }}>
            Where retail meets <span style={{ color: C.cyan }}>intelligence</span>
          </div>
          <div style={{
            fontSize: 12, color: C.textQuaternary,
            lineHeight: 1.8,
          }}>
            © {new Date().getFullYear()} Meridian Registry.<br />All rights reserved.
          </div>
        </div>

        <FooterCol title="Platform" links={[
          { label: 'API Documentation', to: '/api-docs' },
          { label: 'Request a Demo', to: '/request-demo' },
          { label: 'FAQ', to: '/faq' },
          { label: 'Contact', href: 'mailto:admin@meridianregistry.com' },
        ]} linkStyle={linkStyle} onEnter={onEnter} onLeave={onLeave} />

        <FooterCol title="Legal" links={[
          { label: 'Privacy Policy', to: '/privacy' },
          { label: 'Terms of Service', to: '/terms' },
          { label: 'Cookie Policy', to: '/cookies' },
        ]} linkStyle={linkStyle} onEnter={onEnter} onLeave={onLeave} />
      </div>
    </footer>
  );
}

type FooterLink = { label: string; to?: string; href?: string };

function FooterCol({
  title, links, linkStyle, onEnter, onLeave,
}: {
  title: string;
  links: FooterLink[];
  linkStyle: React.CSSProperties;
  onEnter: (e: React.MouseEvent<HTMLElement>) => void;
  onLeave: (e: React.MouseEvent<HTMLElement>) => void;
}) {
  return (
    <div>
      <div style={{
        fontSize: 11, fontWeight: 600,
        letterSpacing: '0.18em', textTransform: 'uppercase',
        color: C.cyan, marginBottom: 16,
      }}>{title}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {links.map(l =>
          l.to ? (
            <Link key={l.label} to={l.to} style={linkStyle} onMouseEnter={onEnter} onMouseLeave={onLeave}>{l.label}</Link>
          ) : (
            <a key={l.label} href={l.href} style={linkStyle} onMouseEnter={onEnter} onMouseLeave={onLeave}>{l.label}</a>
          )
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
 * BUTTONS
 * ═══════════════════════════════════════════════════════════════ */

function PrimaryBtn({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      onMouseEnter={e => { e.currentTarget.style.background = C.cyanLight; e.currentTarget.style.borderColor = C.cyanLight; }}
      onMouseLeave={e => { e.currentTarget.style.background = C.cyan; e.currentTarget.style.borderColor = C.cyan; }}
      style={{
        fontFamily: F.ui,
        fontWeight: 600, fontSize: 15,
        padding: '14px 26px', borderRadius: 12,
        background: C.cyan, color: '#000',
        border: `1px solid ${C.cyan}`,
        boxShadow: `0 0 0 1px ${C.cyanGlow}, 0 8px 24px -8px ${C.cyanGlow}`,
        cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', gap: 6,
        transition: 'all 120ms ease',
      }}>
      {children}
    </button>
  );
}

function ScrollBtn({ href, children }: { href: string; children: React.ReactNode }) {
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    const target = document.querySelector(href);
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  return (
    <a
      href={href}
      onClick={handleClick}
      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; e.currentTarget.style.color = C.textPrimary; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = C.textSecondary; }}
      style={{
        fontFamily: F.ui, fontWeight: 500, fontSize: 15,
        padding: '14px 26px', borderRadius: 12,
        background: 'transparent',
        color: C.textSecondary,
        border: '1px solid transparent',
        cursor: 'pointer', textDecoration: 'none',
        display: 'inline-flex', alignItems: 'center', gap: 6,
        transition: 'all 120ms ease',
      }}>
      {children}
    </a>
  );
}

function GhostBtn({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      onMouseEnter={e => { e.currentTarget.style.background = C.bgElevated; e.currentTarget.style.borderColor = C.borderStrong; }}
      onMouseLeave={e => { e.currentTarget.style.background = C.bgCard; e.currentTarget.style.borderColor = C.borderDefault; }}
      style={{
        fontFamily: F.ui, fontWeight: 500, fontSize: 13,
        padding: '8px 14px', borderRadius: 10,
        border: `1px solid ${C.borderDefault}`,
        background: C.bgCard, color: C.textPrimary,
        cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', gap: 6,
        transition: 'all 120ms ease',
      }}>
      {children}
    </button>
  );
}
